import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import LogoBlue from "../assets/logo-blue.png";
import LogoWhite from "../assets/logo-white.png";

interface NavLinkProps {
  to: string;
  label: string;
  hasDropdown?: boolean;
  dropdownItems?: { label: string; to: string }[];
}

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks: NavLinkProps[] = [
    { to: '/', label: 'Home' },
    {
      to: '/services',
      label: 'Services',
      hasDropdown: true,
      dropdownItems: [
        { label: 'MVP Development', to: '/mvp-development' },
        { label: 'Digital Transformation', to: '/digital-transformation' },
        { label: 'AI Services', to: '/ai-services' },
        { label: 'Web Development', to: '/services#web-development' },
        { label: 'Mobile App Development', to: '/services#mobile-development' },
        { label: 'UI/UX Design', to: '/services#ui-ux-design' },
        { label: 'Data Analytics', to: '/services#data-analytics' },
      ]
    },
    { to: '/about', label: 'About Us' },
    { to: '/contact', label: 'Contact Us' },
  ];

  const toggleDropdown = (label: string) => {
    if (activeDropdown === label) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(label);
    }
  };

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled || isOpen ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      } ${
        scrolled ? 'md:bg-white shadow-md py-2' : 'md:bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <div >
            <img className='h-8 w-9' alt={"logo"} src={scrolled || isOpen?LogoWhite:LogoBlue} />
            </div>
            <span className={`text-2xl pl-2 font-inter font-bold bg-clip-text ${isOpen || scrolled?'bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent':'text-white'} ${scrolled?'md:bg-gradient-to-r md:from-blue-600 md:to-indigo-600 md:text-transparent':'md:text-white'}`}>
              Techno-Stack-Labs
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group hover:scale-110">
                {link.hasDropdown ? (
                  <button 
                  className={`flex items-center ${scrolled?"text-gray-700":"text-white"} hover:scale-110 font-medium`}
                    onClick={() => toggleDropdown(link.label)}
                  >
                    {link.label}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                ) : (
                  <Link
                    to={link.to}
                    className={`${scrolled?"text-gray-700":"text-white"} hover:scale-110 font-medium`}
                  >
                    {link.label}
                  </Link>
                )}

                {link.hasDropdown && (
                  <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-1">
                      {link.dropdownItems?.map((item) => (
                        <Link
                          key={item.label}
                          to={item.to}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <Link 
              to="https://blog.technostacklabs.com/"
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${scrolled?"text-gray-700":"text-white"} hover:scale-110 font-medium`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition duration-300 shadow-md"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-blue-600 focus:outline-none"
            >
              {isOpen ? (
                <X color={scrolled || isOpen?"grey":"white"} className="h-6 w-6" />
              ) : ( 
                <Menu color={scrolled || isOpen?"grey":"white"} className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-white mt-4 pb-4">
            <div className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <div key={link.label} className="py-2">
                  {link.hasDropdown ? (
                    <div>
                      <button 
                        className="flex items-center justify-between w-full text-gray-700 hover:text-blue-600 font-medium"
                        onClick={() => toggleDropdown(link.label)}
                      >
                        {link.label}
                        <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                      </button>
                      {activeDropdown === link.label && (
                        <div className="mt-2 pl-4 border-l-2 border-gray-200">
                          {link.dropdownItems?.map((item) => (
                            <Link
                              key={item.label}
                              to={item.to}
                              className="block py-2 text-sm text-gray-600 hover:text-blue-600"
                              onClick={() => setIsOpen(false)}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={link.to}
                      className="text-gray-700 hover:text-blue-600 font-medium"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link 
              to="https://blog.technostacklabs.com/"
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${(scrolled || isOpen)?"text-gray-700":"text-white"} hover:scale-110 font-medium`}
            >
              Blog
            </Link>
              <Link
                to="/contact"
                className="mt-4 px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition duration-300 shadow-md text-center"
                onClick={() => setIsOpen(false)}
              >
                Get Quote
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;